import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { clearActiveConversation } from '../store/conversationSlice';
import GradientText from '../components/LandingPage/GradientText';
import LaunchVideo from '../assets/videos/launch.mp4';
import AgentsVideo from '../assets/videos/agents.mp4';
import DashboardVideo from '../assets/videos/dashboard.mp4';

import {
  Box,
  Typography,
  Button,
  Divider,
  useMediaQuery,
  Modal,
  IconButton,
} from '@mui/material';

import QueryCarousel from '../components/QueryCarousel';
import AuthModal from '../components/AuthModal';
import AgentTitleBar from '../components/LandingPage/AgentTitleBar';
import QueryInput from '../components/QueryInput';

import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import ViewCarouselRoundedIcon from '@mui/icons-material/ViewCarouselRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

const EXPLAINER_CARD_SX = {
  flex: 1,
  minHeight: 160,
  borderRadius: 6,
  paddingY: 2,
  paddingX: 2,
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  color: 'rgba(0,0,0,0.85)',
  border: '1.5px solid rgba(18, 157, 232, 0.40)',
  background:
    'rgba(18, 157, 232, 0.01)',
  boxShadow:
    'inset 0 1px 0 rgba(255,255,255,0.15), 0 8px 24px rgba(0,122,255,0.05)',
  backdropFilter: 'blur(8px)',
  WebkitBackdropFilter: 'blur(8px)',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.15), 0 12px 32px rgba(0,122,255,0.12)',
  },
};

function AIAdvisorHome() {
  const [input, setInput] = useState('');
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [isSignInMode, setIsSignInMode] = useState(true);
  const [showCarousel, setShowCarousel] = useState(true);
  const [openVideoModal, setOpenVideoModal] = useState(null);

  const [selectedAgent, setSelectedAgent] = useState(['auto']);
  const [selectedTone, setSelectedTone] = useState('professional');

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const authState = useSelector((state) => state.auth);
  const isAuthenticated = authState.isAuthenticated;

  const isNarrow = useMediaQuery('(max-width: 900px)');

  useEffect(() => {
    dispatch(clearActiveConversation());
  }, [dispatch]);

  const handleStartNewConversation = (queryText) => {
    if (!queryText.trim()) return;
    if (!isAuthenticated) {
      setAuthModalOpen(true);
      return;
    }
    navigate('/conversation/new', {
      state: {
        pendingUserQuery: queryText,
        advisorOptions: {
          agent: selectedAgent,
          tone: selectedTone,
        },
      },
    });
  };

  const handlePresetClick = (query) => {
    if (!isAuthenticated) {
      setAuthModalOpen(true);
      return;
    }
    handleStartNewConversation(query);
  };

  const handleSend = () => {
    if (!isAuthenticated) {
      setAuthModalOpen(true);
      return;
    }
    handleStartNewConversation(input);
    setInput('');
  };

  const handleAgentSelect = (agentType, query) => {
    // For agent title bar clicks, set as the only selected agent
    setSelectedAgent([agentType]);
    if (query) {
      // If a sample query was clicked, send it immediately
      if (!isAuthenticated) {
        setAuthModalOpen(true);
        return;
      }
      handleStartNewConversation(query);
    }
  };

  const handleAuthModalClose = () => setAuthModalOpen(false);
  const handleAuthModeToggle = (isSignIn) => setIsSignInMode(isSignIn);

  const toggleButtonLabel = useMemo(
    () => (showCarousel ? 'Explain Vester' : 'Prompt Gallery'),
    [showCarousel]
  );

  const toggleButtonIcon = useMemo(
    () => (showCarousel ? <AutoAwesomeRoundedIcon /> : <ViewCarouselRoundedIcon />),
    [showCarousel]
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'auto', }}>
      {/* Header / Hero */}
      <Box
        sx={{
          px: 3,
          pt: 0,
          pb: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'left',
          textAlign: 'left',
          alignSelf: 'center',
          width: '950px',
          mt: 'auto',
          mb: 'auto',
        }}
      >
        <Box sx={{px: 0, pb: 1}}>
        {/* <Box component="img" src={VesterWordmark} alt="vester" sx={{ height: 60, mb: 1, alignSelf: 'flex-start' }} /> */}
        <Typography
          sx={{
            fontSize: 78,
            fontWeight: 700,
            color: '#129de8',
            fontFamily: 'Signika',
            ml: 0,
            mt: -8,
            mb: -2.5,
          }}
        >
          <GradientText
            colors={["#00D8F5", "#129de8", "#00D8F5", "#129de8", "#00D8F5"]}
            animationSpeed={10}
            showBorder={false}
            className="vester-gradient-text"
          >
            vester
          </GradientText>
        </Typography>
          <Typography
            sx={{
              fontSize: 22,
              fontWeight: 500,
              color: '#0b2a4a',
              fontFamily: 'Inter Tight, Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif',
              ml: 0.2,
              mb: -0.5,
              my: 'auto',
              position: 'relative',
              zIndex: 2,
            }}
          >
            Your personal investment analyst
          </Typography>
        <Typography sx={{ mt: 0, color: '#44474B', ml: 0.4 }}>
          Ask me anything about markets, investments, or your portfolio - I'll create dashboards, slide decks, and reports
        </Typography>
        </Box>

      {/* Agent title bar row + toggle */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'left',
          gap: 1.3,
          flexWrap: 'wrap',
          justifyContent: 'left',
          mx: 0,
          height: 50,
        }}
      >
        <AgentTitleBar agentType="vester" displayName="Vester" onAgentSelect={handleAgentSelect} />
        <AgentTitleBar agentType="quant" displayName="The Quant" onAgentSelect={handleAgentSelect} />
        <AgentTitleBar agentType="news" displayName="News Agent" onAgentSelect={handleAgentSelect} />
        <AgentTitleBar agentType="onchain" displayName="On-Chain Analyst" onAgentSelect={handleAgentSelect} />
        <AgentTitleBar agentType="portfolio" displayName="Portfolio Manager" onAgentSelect={handleAgentSelect} />
        <AgentTitleBar agentType="risk" displayName="Risk Manager" onAgentSelect={handleAgentSelect} />

        {/* Vertical divider between Risk Manager and toggle */}
        <Divider
          orientation="vertical"
          flexItem
          sx={{
            alignSelf: 'center',
            alignItems: 'center',
            justifyContent: 'center',
            height: 36,
            borderColor: 'rgba(0,0,0,0.12)',
            mb: 1,
            mr: -0.25
          }}
        />

        <Button
          size="small"
          variant="outlined"
          onClick={() => setShowCarousel((s) => !s)}
          startIcon={toggleButtonIcon}
          sx={{
            borderRadius: '12px',
            textTransform: 'none',
            height: 42,
            fontSize: 11,
            px: 1.5,
            py: 0.5,
            mb: 0.5,
            background: 'rgba(18, 157, 232, 0.0)',
            border: '1px solid rgba(205, 205, 205, 0.6)',
            color: '#1b1f23',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
          }}
        >
          {toggleButtonLabel}
        </Button>
      </Box>

      {/* Carousel / Explainers */}
      <Box sx={{ px: 0, mt: 0, mb: 0 }}>
        {showCarousel ? (
          <Box sx={{ width: '100%', mt: 0.8 }}>
            <QueryCarousel onItemClick={handlePresetClick} />
          </Box>
        ) : (
          <Box
            sx={{
              maxWidth: 1200,
              mx: 'auto',
              display: 'grid',
              gridTemplateColumns: isNarrow ? '1fr' : 'repeat(3, 1fr)',
              gap: 1,
              mb: 2.5,
              mt: 1.5,
            }}
          >
            <Box sx={EXPLAINER_CARD_SX} onClick={() => setOpenVideoModal('launch')}>
              <Typography sx={{ fontWeight: 600, fontSize: 16, mt: 0 }}>Launch Research Agents</Typography>
              <Typography sx={{ fontSize: 13, opacity: 0.85 }}>
                Ask your question and press enter. Vester will select the best agent mix and begin research.
              </Typography>
              <Box
                sx={{
                  mt: 'auto',
                  height: 160,
                  borderRadius: 4,
                  border: 'none',
                  background: 'rgba(255,255,255,0.45)',
                  display: 'grid',
                  placeItems: 'center',
                  fontSize: 12,
                  color: 'rgba(0,0,0,0.55)',
                  overflow: 'hidden',
                }}
              >
                <Box
                  component="video"
                  src={LaunchVideo}
                  alt="Launch research agents demo"
                  autoPlay
                  loop
                  muted
                  playsInline
                  loading="lazy"
                  preload="none"
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
            </Box>
            <Box sx={EXPLAINER_CARD_SX} onClick={() => setOpenVideoModal('agents')}>
              <Typography sx={{ fontWeight: 600, fontSize: 16, mt: 0 }}>Agents at work</Typography>
              <Typography sx={{ fontSize: 13, opacity: 0.85 }}>
                Agents pull data, develop visuals, and surface insights you can act on immediately.
              </Typography>
              <Box
                sx={{
                  mt: 'auto',
                  height: 160,
                  borderRadius: 4,
                  border: 'none',
                  background: 'rgba(255,255,255,0.45)',
                  display: 'grid',
                  placeItems: 'center',
                  fontSize: 12,
                  color: 'rgba(0,0,0,0.55)',
                  overflow: 'hidden',
                }}
              >
                <Box
                  component="video"
                  src={AgentsVideo}
                  alt="Agents at work demo"
                  autoPlay
                  loop
                  muted
                  playsInline
                  loading="lazy"
                  preload="none"
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
            </Box>
            <Box sx={EXPLAINER_CARD_SX} onClick={() => setOpenVideoModal('dashboard')}>
              <Typography sx={{ fontWeight: 600, fontSize: 16, mt: 0 }}>Save to Dashboard</Typography>
              <Typography sx={{ fontSize: 13, opacity: 0.85 }}>
                Review findings and save visuals to custom dashboards for later tracking.
              </Typography>
              <Box
                sx={{
                  mt: 'auto',
                  height: 160,
                  borderRadius: 4,
                  border: 'none',
                  background: 'rgba(255,255,255,0.45)',
                  display: 'grid',
                  placeItems: 'center',
                  fontSize: 12,
                  color: 'rgba(0,0,0,0.55)',
                  overflow: 'hidden',
                }}
              >
                <Box
                  component="video"
                  src={DashboardVideo}
                  alt="Save to dashboard demo"
                  autoPlay
                  loop
                  muted
                  playsInline
                  loading="lazy"
                  preload="none"
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {/* Query input with integrated dropdowns */}
      <Box sx={{ px: 0, pt: 0, pb: 0, maxWidth: 1080, mx: 'auto', width: '100%' }}>
        <QueryInput
          value={input}
          onChange={(val) => setInput(val)}
          onSend={handleSend}
          selectedAgent={selectedAgent}
          onAgentChange={setSelectedAgent}
          selectedTone={selectedTone}
          onToneChange={setSelectedTone}
        />
      </Box>
      </Box>

      {/* Trending Insights & Dashboards
      <Box sx={{ px: 3, pb: 4, pt: 2, maxWidth: 1200, mx: 'auto', width: '100%' }}>
        <Typography sx={{ fontWeight: 700, mb: 3 }}>Trending Insights &amp; Dashboards</Typography>
        <Grid container spacing={3}>
          {Array.from({ length: 6 }).map((_, idx) => (
            <Grid key={idx} item xs={12} sm={6} md={4}>
              <Box
                sx={{
                  height: 220,
                  borderRadius: 4,
                  background: 'linear-gradient(180deg, #f3f4f6 0%, #eceff1 100%)',
                  border: '1px solid rgba(0,0,0,0.06)',
                }}
              />
            </Grid>
          ))}
        </Grid>
      </Box> */}

      {/* Video Modal */}
      <Modal
        open={openVideoModal !== null}
        onClose={() => setOpenVideoModal(null)}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0,0,0,0.1)',
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '90vw',
            maxWidth: 800,
            bgcolor: 'background.paper',
            borderRadius: 6,
            boxShadow: 24,
            p: 4,
            outline: 'none',
            color: 'rgba(0,0,0,0.85)',
            border: '1.5px solid rgba(18, 157, 232, 0.40)',
            background: 'rgba(18, 157, 232, 0.1)',
            backdropFilter: 'blur(17px)',
            WebkitBackdropFilter: 'blur(17px)',
          }}
        >
          <IconButton
            onClick={() => setOpenVideoModal(null)}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 10,
              bgcolor: 'rgba(0,0,0,0.05)',
              color: 'rgba(0,0,0,0.6)',
              '&:hover': {
                bgcolor: 'rgba(0,0,0,0.1)',
              },
            }}
          >
            <CloseRoundedIcon />
          </IconButton>

          {openVideoModal === 'launch' && (
            <>
              <Typography sx={{ fontWeight: 600, fontSize: 24, mb: 1.5 }}>Launch Research Agents</Typography>
              <Typography sx={{ fontSize: 16, mb: 3 }}>
                Ask your question and press enter. Vester will select the best agent mix and begin research.
              </Typography>
            </>
          )}

          {openVideoModal === 'agents' && (
            <>
              <Typography sx={{ fontWeight: 600, fontSize: 24, mb: 1.5 }}>Agents at work</Typography>
              <Typography sx={{ fontSize: 16, mb: 3 }}>
                Agents pull data, develop visuals, and surface insights you can act on immediately.
              </Typography>
            </>
          )}

          {openVideoModal === 'dashboard' && (
            <>
              <Typography sx={{ fontWeight: 600, fontSize: 24, mb: 1.5 }}>Save to Dashboard</Typography>
              <Typography sx={{ fontSize: 16, mb: 3 }}>
                Review findings and save visuals to custom dashboards for later tracking.
              </Typography>
            </>
          )}

          <Box
            sx={{
              borderRadius: 4,
              border: 'none',
              background: 'rgba(255,255,255,0.45)',
              overflow: 'hidden',
              aspectRatio: '16/9',
            }}
          >
            <Box
              component="video"
              src={
                openVideoModal === 'launch'
                  ? LaunchVideo
                  : openVideoModal === 'agents'
                  ? AgentsVideo
                  : DashboardVideo
              }
              autoPlay
              loop
              muted
              sx={{
                width: '100%',
                height: '100%',
                display: 'block',
                objectFit: 'cover',
              }}
            />
          </Box>
        </Box>
      </Modal>

      {/* Auth Modal */}
      <AuthModal
        open={authModalOpen}
        onClose={handleAuthModalClose}
        showSignIn={isSignInMode}
        onToggleMode={handleAuthModeToggle}
      />
    </Box>
  );
}

export default AIAdvisorHome;
